import React, { useMemo, useState, useCallback } from 'react';
import { geocodeAPI } from '../services/api';

// Dynamic import for Leaflet (client-side only)
let MapContainer, TileLayer, Marker, Popup;
let L;

const MapComponent = ({ personsWithCoords, selectedLocation, onPersonSelect }) => {
  const [mapReady, setMapReady] = useState(false);

  React.useEffect(() => {
    import('leaflet').then((leafletModule) => {
      L = leafletModule.default;
      import('react-leaflet').then((reactLeafletModule) => {
        MapContainer = reactLeafletModule.MapContainer;
        TileLayer = reactLeafletModule.TileLayer;
        Marker = reactLeafletModule.Marker;
        Popup = reactLeafletModule.Popup;
        setMapReady(true);
      });
    });

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const createIcon = useCallback((isSelected) => {
    if (!L) return null;
    return L.divIcon({
      className: 'custom-marker',
      html: `<div style="
        width: 30px;
        height: 30px;
        background: ${isSelected ? '#00d4ff' : '#00ff88'};
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 2px 10px rgba(0,0,0,0.3);
      "></div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 30],
      popupAnchor: [0, -30]
    });
  }, []);

  if (!mapReady) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-tech-surface">
        <div className="animate-spin w-8 h-8 border-2 border-tech-accent border-t-transparent rounded-full" />
      </div>
    );
  }

  const bounds = personsWithCoords.length > 1 
    ? [
        [Math.min(...personsWithCoords.map(p => parseFloat(p.latitude))) - 0.1,
         Math.min(...personsWithCoords.map(p => parseFloat(p.longitude))) - 0.1],
        [Math.max(...personsWithCoords.map(p => parseFloat(p.latitude))) + 0.1,
         Math.max(...personsWithCoords.map(p => parseFloat(p.longitude))) + 0.1]
      ]
    : null;

  const center = personsWithCoords.length === 1 
    ? [parseFloat(personsWithCoords[0].latitude), parseFloat(personsWithCoords[0].longitude)]
    : personsWithCoords.length > 1
      ? [
          (Math.min(...personsWithCoords.map(p => parseFloat(p.latitude))) + 
           Math.max(...personsWithCoords.map(p => parseFloat(p.latitude)))) / 2,
          (Math.min(...personsWithCoords.map(p => parseFloat(p.longitude))) + 
           Math.max(...personsWithCoords.map(p => parseFloat(p.longitude)))) / 2
        ]
      : [20, 0];

  const getZoomLevel = () => {
    if (personsWithCoords.length === 0) return 3;
    if (personsWithCoords.length === 1) return 12;
    
    const lats = personsWithCoords.map(p => parseFloat(p.latitude));
    const lngs = personsWithCoords.map(p => parseFloat(p.longitude));
    const latSpread = Math.max(...lats) - Math.min(...lats);
    const lngSpread = Math.max(...lngs) - Math.min(...lngs);
    const maxSpread = Math.max(latSpread, lngSpread);
    
    if (maxSpread < 0.1) return 14;
    if (maxSpread < 0.5) return 11;
    if (maxSpread < 1) return 9;
    if (maxSpread < 5) return 7;
    if (maxSpread < 20) return 5;
    if (maxSpread < 50) return 4;
    return 3;
  };

  return (
    <MapContainer
      center={center}
      zoom={getZoomLevel()}
      bounds={bounds}
      boundsOptions={{ padding: [30, 30], maxZoom: 15 }}
      style={{ height: '100%', width: '100%' }}
      scrollWheelZoom={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {personsWithCoords.map((person) => (
        <Marker
          key={person.id}
          position={[parseFloat(person.latitude), parseFloat(person.longitude)]}
          icon={createIcon(selectedLocation?.id === person.id)}
          eventHandlers={{
            click: () => onPersonSelect(person.id)
          }}
        >
          <Popup>
            <div className="text-center">
              <p className="font-semibold">{person.name}</p>
              <p className="text-sm text-gray-500">{person.address}</p>
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

const getBirthMonthDay = (birthDate) => {
  if (!birthDate) return null;
  const cleaned = birthDate.trim().replace(/\s+/g, ' ');
  // Stored format is European DD.MM.YYYY (or DD/MM/YYYY). Match it first so
  // ambiguous dates like 07.04.2002 aren't misread as American MM.DD.YYYY.
  const euroMatch = cleaned.match(/^(\d{1,2})[./](\d{1,2})[./](\d{2,4})$/);
  if (euroMatch) {
    const day = parseInt(euroMatch[1], 10);
    const month = parseInt(euroMatch[2], 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return { month: month - 1, day };
    }
  }
  // Fallback: let the engine handle other formats (ISO YYYY-MM-DD, etc.)
  const date = new Date(cleaned);
  if (!isNaN(date.getTime())) {
    return { month: date.getMonth(), day: date.getDate() };
  }
  return null;
};

const OverviewPanel = ({ persons, stats, onPersonSelect, onDataRefresh }) => {
  const [geocoding, setGeocoding] = useState({});
  const [selectedLocation, setSelectedLocation] = useState(null);

  const shouldGeocodeCount = useMemo(() => {
    return persons.filter(p => {
      if (p.latitude && p.longitude) return false;
      const hasStreetAndCity = p.street && p.city;
      const hasCityAndCountry = p.city && p.country;
      return hasStreetAndCity || hasCityAndCountry;
    }).length;
  }, [persons]);

  const geocodingCount = useMemo(() => {
    return Object.values(geocoding).filter(Boolean).length;
  }, [geocoding]);

  const buildAddress = (person) => {
    const parts = [];
    if (person.street) parts.push(person.street);
    if (person.city) parts.push(person.city);
    if (person.country) parts.push(person.country);
    return parts.filter(Boolean).join(', ');
  };

  const autoGeocodeAll = useCallback(async () => {
    const personsNeedingGeocode = persons.filter(p => {
      if (p.latitude && p.longitude) return false;
      const hasStreetAndCity = p.street && p.city;
      const hasCityAndCountry = p.city && p.country;
      return hasStreetAndCity || hasCityAndCountry;
    });
    
    if (personsNeedingGeocode.length === 0) return;
    
    for (const person of personsNeedingGeocode) {
      const address = buildAddress(person);
      if (!address) continue;
      
      setGeocoding(prev => ({ ...prev, [person.id]: true }));
      
      try {
        await geocodeAPI.geocode(address, person.id);
      } catch (err) {
        console.error('Geocode failed for:', person.name, err);
      } finally {
        setGeocoding(prev => ({ ...prev, [person.id]: false }));
      }
      
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    if (onDataRefresh) {
      onDataRefresh();
    }
  }, [persons, onDataRefresh]);

  const handleGeocode = async (person) => {
    const address = buildAddress(person);
    if (!address) return;

    setGeocoding(prev => ({ ...prev, [person.id]: true }));
    
    try {
      await geocodeAPI.geocode(address, person.id);
      if (onDataRefresh) onDataRefresh();
    } catch (err) {
      console.error('Geocode failed:', err);
    } finally {
      setGeocoding(prev => ({ ...prev, [person.id]: false }));
    }
  };

  // Get persons with location data - defined before handlers
  const personsWithLocation = useMemo(() => {
    return persons.filter(p => 
      (p.latitude && p.longitude) || 
      (p.street && p.city) ||
      (p.city && p.country)
    ).map(p => {
      const address = [p.street, p.city, p.country].filter(Boolean).join(', ');
      return {
        ...p,
        address: address || (p.latitude && p.longitude ? `${p.latitude}, ${p.longitude}` : ''),
        hasCoords: Boolean(p.latitude && p.longitude)
      };
    });
  }, [persons]);

  const personsWithCoords = useMemo(() => {
    return personsWithLocation.filter(p => p.hasCoords);
  }, [personsWithLocation]);

  // Get all birthdays sorted by nearest
  const nearestBirthdays = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentDay = now.getDate();
    
    return persons
      .filter(p => p.birthDate)
      .map(p => {
        const parsed = getBirthMonthDay(p.birthDate);
        if (!parsed) return null;
        
        const { month, day } = parsed;
        let nextBirthday;
        let year = currentYear;
        
        if (month > currentMonth || (month === currentMonth && day >= currentDay)) {
          nextBirthday = new Date(year, month, day);
        } else {
          year = currentYear + 1;
          nextBirthday = new Date(year, month, day);
        }
        
        const daysUntil = Math.ceil((nextBirthday - now) / (1000 * 60 * 60 * 24));
        
        return { 
          ...p, 
          parsedMonth: month, 
          parsedDay: day,
          nextBirthday, 
          daysUntil,
          isBirthdayToday: month === currentMonth && day === currentDay
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.parsedMonth !== b.parsedMonth) {
          return a.parsedMonth - b.parsedMonth;
        }
        return a.parsedDay - b.parsedDay;
      })
      .sort((a, b) => {
        if (a.parsedMonth > currentMonth || (a.parsedMonth === currentMonth && a.parsedDay >= currentDay)) {
          if (b.parsedMonth > currentMonth || (b.parsedMonth === currentMonth && b.parsedDay >= currentDay)) {
            return a.daysUntil - b.daysUntil;
          }
          return -1;
        } else {
          if (b.parsedMonth > currentMonth || (b.parsedMonth === currentMonth && b.parsedDay >= currentDay)) {
            return 1;
          }
          return a.daysUntil - b.daysUntil;
        }
      });
  }, [persons]);

  // Get recently accessed
  const recentPersons = useMemo(() => {
    return [...persons]
      .filter(p => p.last_accessed || p.updated_at || p.created_at)
      .sort((a, b) => {
        const dateA = new Date(a.last_accessed || a.updated_at || a.created_at || 0);
        const dateB = new Date(b.last_accessed || b.updated_at || b.created_at || 0);
        return dateB - dateA;
      })
      .slice(0, 8);
  }, [persons]);

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'JUST NOW';
    if (minutes < 60) return `${minutes}M AGO`;
    if (hours < 24) return `${hours}H AGO`;
    if (days < 7) return `${days}D AGO`;
    return date.toLocaleDateString();
  };

  const handleLocationClick = (personId) => {
    const person = personsWithCoords.find(p => p.id === personId);
    setSelectedLocation(person);
    onPersonSelect(personId);
  };

  return (
    <div className="h-full overflow-auto p-6 relative animate-fade-in">
      <div className="max-w-[1800px] mx-auto space-y-6">
        
        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4">
          <div className="tech-card p-5">
            <p className="text-tech-textDim text-xs font-mono uppercase mb-2">Total Records</p>
            <p className="text-3xl font-mono font-bold text-tech-text">{stats.total}</p>
          </div>
          <div className="tech-card p-5">
            <p className="text-tech-textDim text-xs font-mono uppercase mb-2">With Location</p>
            <p className="text-3xl font-mono font-bold text-tech-success">{personsWithLocation.length}</p>
          </div>
          <div className="tech-card p-5">
            <p className="text-tech-textDim text-xs font-mono uppercase mb-2">Birthdays</p>
            <p className="text-3xl font-mono font-bold text-tech-warning">{nearestBirthdays.length}</p>
          </div>
          <div className="tech-card p-5">
            <p className="text-tech-textDim text-xs font-mono uppercase mb-2">Active Pins</p>
            <p className="text-3xl font-mono font-bold text-tech-accent">{personsWithCoords.length}</p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-12 gap-6">
          
          {/* Map */}
          <div className="col-span-8 tech-card overflow-hidden">
            <div className="px-5 py-4 border-b border-tech-border flex items-center justify-between bg-tech-surface/30">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded bg-tech-success/20 border border-tech-success/50 flex items-center justify-center">
                  <svg className="w-4 h-4 text-tech-success" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                </div>
                <span className="text-sm font-mono font-semibold text-tech-text">LOCATION MAP</span>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={autoGeocodeAll}
                  disabled={geocodingCount > 0 || shouldGeocodeCount === 0}
                  className="px-3 py-1.5 text-xs font-mono bg-tech-accent/10 hover:bg-tech-accent/20 border border-tech-accent/30 text-tech-accent rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {geocodingCount > 0 ? `GEOCODING... (${geocodingCount})` : `GEOCODE ALL (${shouldGeocodeCount})`}
                </button>
                <span className="text-tech-accent text-sm font-mono">{personsWithCoords.length} PINS</span>
              </div>
            </div>
            <div className="p-5">
              {personsWithLocation.length > 0 ? (
                <div className="relative">
                  <div 
                    className="relative w-full rounded-lg border border-tech-border overflow-hidden"
                    style={{ height: '500px' }}
                  >
                    <MapComponent
                      personsWithCoords={personsWithCoords}
                      selectedLocation={selectedLocation}
                      onPersonSelect={handleLocationClick}
                    />
                  </div>

                  {selectedLocation && (
                    <button
                      onClick={() => onPersonSelect(selectedLocation.id)}
                      className="mt-4 w-full tech-card-elevated p-4 text-left hover:border-tech-accent/50 transition flex items-center gap-4"
                    >
                      <div className="w-14 h-14 rounded-lg bg-tech-accent/20 border-2 border-tech-accent/50 flex items-center justify-center">
                        <span className="text-tech-accent text-xl font-mono font-bold">{selectedLocation.name.charAt(0)}</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-lg font-medium text-tech-text">{selectedLocation.name}</p>
                        <p className="text-tech-textDim text-sm">{selectedLocation.address}</p>
                      </div>
                      <svg className="w-6 h-6 text-tech-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}

                  <div className="mt-4 grid grid-cols-3 gap-3">
                    {personsWithLocation.slice(0, 6).map((person) => (
                      <div
                        key={person.id}
                        className={`tech-card-elevated p-3 flex items-center gap-3 cursor-pointer transition ${
                          selectedLocation?.id === person.id ? 'border-tech-accent bg-tech-accent/5' : ''
                        }`}
                        onClick={() => setSelectedLocation(person)}
                      >
                        <button
                          onClick={() => handleLocationClick(person.id)}
                          className="flex-1 flex items-center gap-3 min-w-0"
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                            person.hasCoords 
                              ? 'bg-tech-success/20 border-2 border-tech-success/50' 
                              : 'bg-tech-surface border border-tech-border'
                          }`}>
                            {person.hasCoords ? (
                              <svg className="w-5 h-5 text-tech-success" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                              </svg>
                            ) : (
                              <span className="text-sm font-mono text-tech-textMuted">{person.name.charAt(0)}</span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-tech-text font-medium truncate">{person.name}</p>
                            {person.hasCoords && (
                              <p className="text-[10px] text-tech-textDim font-mono">
                                {parseFloat(person.latitude).toFixed(3)}, {parseFloat(person.longitude).toFixed(3)}
                              </p>
                            )}
                          </div>
                        </button>
                        {!person.hasCoords && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleGeocode(person); }}
                            disabled={geocoding[person.id]}
                            className="shrink-0 p-2 bg-tech-accent/10 hover:bg-tech-accent/20 rounded-lg transition"
                            title="Geocode"
                          >
                            {geocoding[person.id] ? (
                              <svg className="w-4 h-4 animate-spin text-tech-accent" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-tech-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            )}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-24 h-24 rounded-2xl bg-tech-surface border-2 border-tech-border flex items-center justify-center mb-5">
                    <svg className="w-12 h-12 text-tech-textMuted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <p className="text-lg font-mono text-tech-textMuted">NO LOCATIONS</p>
                  <p className="text-sm text-tech-textDim mt-2">Add address or coordinates to persons</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="col-span-4 space-y-6">
            
            {/* Birthdays */}
            <div className="tech-card">
              <div className="px-5 py-4 border-b border-tech-border flex items-center gap-3 bg-tech-warning/10">
                <div className="w-8 h-8 rounded bg-tech-warning/20 border border-tech-warning/50 flex items-center justify-center">
                  <svg className="w-4 h-4 text-tech-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="text-sm font-mono font-semibold text-tech-text">UPCOMING BIRTHDAYS</span>
              </div>
              <div className="p-4">
                {nearestBirthdays.length > 0 ? (
                  <div className="space-y-2">
                    {nearestBirthdays.slice(0, 4).map((person) => (
                      <button
                        key={person.id}
                        onClick={() => handleLocationClick(person.id)}
                        className="w-full flex items-center gap-4 p-3 rounded-lg bg-tech-surface/30 hover:bg-tech-surface/50 transition text-left"
                      >
                        <div className="w-12 h-12 rounded-xl bg-tech-warning/10 border-2 border-tech-warning/30 flex items-center justify-center shrink-0">
                          <span className="text-tech-warning text-sm font-mono font-bold">
                            {person.parsedDay}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-tech-text truncate">{person.name}</p>
                          <p className="text-tech-textDim text-xs">
                            {person.nextBirthday.toLocaleDateString('en-US', { month: 'short' })}
                          </p>
                        </div>
                        <span className={`text-xs font-mono px-2 py-1 rounded ${
                          person.isBirthdayToday 
                            ? 'bg-tech-success text-tech-success' 
                            : 'bg-tech-warning/20 text-tech-warning'
                        }`}>
                          {person.isBirthdayToday ? 'TODAY' : person.daysUntil + ' DAYS'}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-tech-textDim text-sm text-center py-8">No birthdays</p>
                )}
              </div>
            </div>

            {/* Recently Viewed */}
            <div className="tech-card">
              <div className="px-5 py-4 border-b border-tech-border flex items-center gap-3 bg-tech-accent/10">
                <div className="w-8 h-8 rounded bg-tech-accent/20 border border-tech-accent/50 flex items-center justify-center">
                  <svg className="w-4 h-4 text-tech-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="text-sm font-mono font-semibold text-tech-text">RECENTLY VIEWED</span>
              </div>
              <div className="p-4">
                {recentPersons.length > 0 ? (
                  <div className="space-y-2">
                    {recentPersons.map((person) => (
                      <button
                        key={person.id}
                        onClick={() => handleLocationClick(person.id)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg bg-tech-surface/30 hover:bg-tech-surface/50 transition text-left"
                      >
                        <div className="w-10 h-10 rounded-lg bg-tech-surface border border-tech-border flex items-center justify-center shrink-0">
                          <span className="text-tech-textMuted font-mono text-sm font-semibold">
                            {person.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-tech-text truncate">{person.name}</p>
                          <p className="text-tech-textDim text-xs">
                            {formatTime(person.last_accessed || person.updated_at || person.created_at)}
                          </p>
                        </div>
                        <svg className="w-4 h-4 text-tech-textDim shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-tech-textDim text-sm text-center py-8">No recent activity</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewPanel;
